import { useState } from "react";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { Button } from "@/components/ui/buttons/button";
import {
  useFriends,
  useSendFriendRequest,
  useRespondFriendRequest,
  useRemoveFriend,
  useSendKeyGift,
  useClaimKeyGift,
} from "@/hooks/queries/use-social";

export const FriendsView = () => {
  const { data, isLoading, refetch } = useFriends();
  const sendRequest = useSendFriendRequest();
  const respond = useRespondFriendRequest();
  const remove = useRemoveFriend();
  const sendGift = useSendKeyGift();
  const claimGift = useClaimKeyGift();

  const [addInput, setAddInput] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const friends = data?.friends ?? [];
  const accepted = friends.filter((f) => f.status === "ACCEPTED");
  const incoming = friends.filter((f) => f.status === "PENDING" && !f.is_outgoing);
  const outgoing = friends.filter((f) => f.status === "PENDING" && f.is_outgoing);
  const pendingGifts = data?.pending_received_gifts ?? [];

  const handleSendRequest = async () => {
    setAddError(null);
    if (!addInput.trim()) return;
    try {
      await sendRequest.mutateAsync({ player_id: addInput.trim() });
      setAddInput("");
    } catch (err: any) {
      setAddError(err.message ?? "Failed");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-full bg-black/50 backdrop-blur-[5px]">
        <div className="border-4 border-pk-blue-4 border-t-transparent rounded-full w-10 h-10 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 pt-6 pb-20 w-full overflow-y-auto h-full bg-black/50 backdrop-blur-[5px]">
      <StrokedText strokeWidth={10} fontSize={22}>FRIENDS</StrokedText>

      {/* Pending gifts received */}
      {pendingGifts.length > 0 && (
        <StandardFrame variant="yellow">
          <div className="flex flex-col gap-2 p-3">
          <span className="text-pk-night-1 font-bold text-xs uppercase tracking-widest">
            Pending Gifts
          </span>
          {pendingGifts.map((g) => (
            <div key={g.gift_id} className="flex items-center justify-between gap-2">
              <span className="text-pk-night-1 text-sm">
                🎁 From <strong>{g.sender_username}</strong>
              </span>
              <Button
                variant="green"
                className="text-xs px-3 py-1"
                onClick={() => claimGift.mutate({ gift_id: g.gift_id })}
                disabled={claimGift.isPending}
              >
                Claim +1 Key
              </Button>
            </div>
          ))}
          </div>
        </StandardFrame>
      )}

      {/* Add friend by player ID */}
      <StandardFrame variant="blue">
        <div className="flex flex-col gap-2 p-3">
        <span className="text-pk-gray-4 text-xs uppercase tracking-widest">Add Friend</span>
        <div className="flex gap-2">
          <input
            value={addInput}
            onChange={(e) => setAddInput(e.target.value)}
            placeholder="Player ID..."
            className="flex-1 bg-pk-night-1/40 px-3 py-2 border border-pk-gray-4/40 rounded-sm text-white text-sm placeholder:text-pk-gray-4/60 outline-none"
          />
          <Button
            variant="yellow"
            onClick={handleSendRequest}
            disabled={sendRequest.isPending || !addInput.trim()}
          >
            Add
          </Button>
        </div>
        {addError && <span className="text-pk-red-3 text-xs">{addError}</span>}
        </div>
      </StandardFrame>

      {/* Incoming requests */}
      {incoming.length > 0 && (
        <section>
          <p className="text-pk-gray-4 text-xs uppercase tracking-widest mb-2">
            Incoming Requests
          </p>
          <div className="flex flex-col gap-2">
            {incoming.map((f) => (
              <StandardFrame key={f.friendship_id} variant="blue">
                <div className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 text-white text-sm font-bold">{f.username}</span>
                <Button
                  variant="green"
                  className="text-xs px-3 py-1"
                  onClick={() => respond.mutate({ friendship_id: f.friendship_id, response: "accept" })}
                  disabled={respond.isPending}
                >
                  Accept
                </Button>
                <Button
                  variant="red"
                  className="text-xs px-3 py-1"
                  onClick={() => respond.mutate({ friendship_id: f.friendship_id, response: "decline" })}
                  disabled={respond.isPending}
                >
                  Decline
                </Button>
                </div>
              </StandardFrame>
            ))}
          </div>
        </section>
      )}

      {/* Outgoing requests */}
      {outgoing.length > 0 && (
        <section>
          <p className="text-pk-gray-4 text-xs uppercase tracking-widest mb-2">Sent Requests</p>
          <div className="flex flex-col gap-2">
            {outgoing.map((f) => (
              <StandardFrame key={f.friendship_id} variant="blue">
                <div className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 text-white text-sm font-bold">{f.username}</span>
                <span className="text-pk-gray-4 text-xs">Pending...</span>
                <Button
                  variant="red"
                  className="text-xs px-2 py-1"
                  onClick={() => remove.mutate({ friendship_id: f.friendship_id })}
                  disabled={remove.isPending}
                >
                  Cancel
                </Button>
                </div>
              </StandardFrame>
            ))}
          </div>
        </section>
      )}

      {/* Accepted friends */}
      <section>
        <p className="text-pk-gray-4 text-xs uppercase tracking-widest mb-2">
          Friends ({accepted.length})
        </p>
        {accepted.length === 0 ? (
          <p className="text-pk-gray-4 text-xs text-center py-4">No friends yet</p>
        ) : (
          <div className="flex flex-col gap-2">
            {accepted.map((f) => (
              <StandardFrame key={f.friendship_id} variant="blue">
                <div className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 text-white text-sm font-bold">{f.username}</span>
                {f.giftable_today && !f.gift_pending && (
                  <Button
                    variant="yellow"
                    className="text-xs px-3 py-1"
                    onClick={() => sendGift.mutate({ friend_id: f.player_id })}
                    disabled={sendGift.isPending}
                  >
                    🎁 Gift Key
                  </Button>
                )}
                {f.gift_pending && (
                  <span className="text-pk-yellow-1 text-xs">Gift sent</span>
                )}
                <Button
                  variant="red"
                  className="text-xs px-2 py-1"
                  onClick={() => remove.mutate({ friendship_id: f.friendship_id })}
                  disabled={remove.isPending}
                >
                  Remove
                </Button>
                </div>
              </StandardFrame>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
